import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import '../styles/MyCustomOrders.css';

const MyCustomOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchMyOrders();
    }
  }, [user]);

  const fetchMyOrders = async () => {
    try {
      console.log('Fetching custom orders for user:', user.uid, user.email);
      
      // Query by 'email' field (not 'userEmail')
      const q = query(
        collection(db, 'customOrders'),
        where('email', '==', user.email)
      );
      
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      
      console.log('Found custom orders:', ordersData);
      
      // Sort manually by createdAt
      ordersData.sort((a, b) => (b.createdAt || new Date()) - (a.createdAt || new Date()));
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert('Error loading orders: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'confirmed': return '#27ae60';
      case 'in-progress': return '#3498db';
      case 'completed': return '#2ecc71';
      case 'cancelled': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending Review';
      case 'confirmed': return 'Confirmed';
      case 'in-progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  if (!user) {
    return (
      <div className="my-custom-orders">
        <div className="auth-required">
          <h2>Please Sign In</h2>
          <p>You need to be signed in to view your custom orders.</p>
          <Link to="/login" className="btn-primary">Sign In</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="my-custom-orders">
        <div className="loading">Loading your orders...</div>
      </div>
    );
  }

  return (
    <div className="my-custom-orders">
      <div className="orders-header">
        <h1>My Custom Orders</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={fetchMyOrders} className="btn-refresh">
            Refresh
          </button>
          <button 
            onClick={async () => {
              const allOrders = await getDocs(collection(db, 'customOrders'));
              const ordersData = allOrders.docs.map(d => ({id: d.id, ...d.data()}));
              console.log('All custom orders:', ordersData);
              console.log('Your email:', user.email);
              console.log('Matching orders:', ordersData.filter(o => o.userEmail === user.email));
              alert(`Total: ${allOrders.size} orders\nYour email: ${user.email}\nMatching: ${ordersData.filter(o => o.userEmail === user.email).length}\n\nCheck console for details.`);
            }} 
            className="btn-refresh"
          >
            Debug All
          </button>
          <Link to="/custom-order" className="btn-new-order">
            + New Custom Order
          </Link>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="no-orders">
          <div className="no-orders-content">
            <h2>No Custom Orders Yet</h2>
            <p>You haven't placed any custom orders yet. Start by creating your first custom order for your special event!</p>
            <Link to="/custom-order" className="btn-primary">
              Create Custom Order
            </Link>
          </div>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map(order => (
            <div key={order.id} className="order-card">
              <div className="order-header">
                <div className="order-info">
                  <h3>{order.eventType}</h3>
                  <p className="order-date">
                    {order.eventDate} at {order.eventTime}
                  </p>
                </div>
                <span 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(order.status) }}
                >
                  {getStatusText(order.status)}
                </span>
              </div>

              <div className="order-details">
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Venue:</strong>
                    <span>{order.venue}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Guests:</strong>
                    <span>{order.guestCount} people</span>
                  </div>
                  <div className="detail-item">
                    <strong>Contact:</strong>
                    <span>{order.phone}</span>
                  </div>
                  {order.budget && (
                    <div className="detail-item">
                      <strong>Budget:</strong>
                      <span>{order.budget}</span>
                    </div>
                  )}
                </div>

                <div className="order-description">
                  <strong>Event Description:</strong>
                  <p>{order.eventDescription}</p>
                </div>

                {order.specialRequirements && (
                  <div className="order-description">
                    <strong>Special Requirements:</strong>
                    <p>{order.specialRequirements}</p>
                  </div>
                )}

                <div className="order-meta">
                  <span className="submitted-date">
                    Submitted: {order.createdAt?.toLocaleDateString()} {order.createdAt?.toLocaleTimeString()}
                  </span>
                </div>
              </div>

              {order.status === 'pending' && (
                <div className="order-status-info">
                  <p>📞 We will contact you within 24 hours to discuss your requirements and provide a quote.</p>
                </div>
              )}

              {order.status === 'confirmed' && (
                <div className="order-status-info">
                  <p>✅ Your order has been confirmed! We'll be in touch with final details soon.</p>
                </div>
              )}

              {order.status === 'completed' && (
                <div className="order-status-info">
                  <p>🎉 Thank you for choosing MD Sweets Caterers! We hope your event was amazing.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCustomOrders;